import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";

import { clsx } from "@alpacahq/ui/old";

type BaseButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  leftIcon?: ReactNode;
  loading?: boolean;
  loadingPlaceholder?: string;
};

type DefaultButtonProps = BaseButtonProps & {
  variant?: "default";
};
type SecondaryButtonProps = BaseButtonProps & {
  variant?: "secondary";
};

type AltButtonProps = BaseButtonProps & {
  variant: "alternate";
};

type GhosButtonProps = BaseButtonProps & {
  variant: "ghost";
};

type GreenButtonProps = BaseButtonProps & {
  variant: "green";
};

type RedButtonProps = BaseButtonProps & {
  variant: "red";
};

export type ButtonProps =
  | DefaultButtonProps
  | SecondaryButtonProps
  | AltButtonProps
  | GhosButtonProps
  | GreenButtonProps
  | RedButtonProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      disabled,
      variant = "default",
      leftIcon,
      loading = false,
      loadingPlaceholder = "Loading...",
      className,
      ...props
    },
    ref,
  ) => {
    // styles for each variant of the button
    const styles = {
      default:
        "text-yellow-900 bg-yellow-400 focus:ring-2 focus:ring-yellow-200",
      secondary:
        "text-zinc-900 focus:outline-none bg-zinc-200 hover:text-zinc-900 focus:z-10 focus:ring-2 focus:ring-zinc-300",
      alternate:
        "text-zinc-900 focus:outline-none bg-white border border-zinc-300 hover:text-zinc-900 focus:z-10 focus:ring-2 focus:ring-zinc-100",
      ghost: "text-gray-900 hover:text-gray-500",
      green: "text-white bg-emerald-500 focus:ring-4 focus:ring-emerald-200",
      red: "text-white bg-rose-500 focus:ring-1 focus:ring-rose-200",
    };

    // get the style based on variant for the button
    const style = styles[variant];

    // this is the spinner that will be shown when loading is true
    const spinner = (
      <svg
        aria-hidden="true"
        className="mr-2 h-4 w-4 animate-spin"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
          opacity="0.4"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentColor"
        />
      </svg>
    );

    return (
      <button
        {...props}
        ref={ref}
        disabled={disabled || loading}
        onFocus={(event) => disabled && event.currentTarget.blur()}
        className={clsx(
          "flex h-10 items-center justify-center space-x-2 rounded-lg p-2.5 text-center text-base focus:outline-none",
          disabled && "cursor-not-allowed opacity-50",
          style,
          className,
        )}
        style={{ fontWeight: 450 }}
      >
        {loading ? (
          <>
            {spinner}
            {loadingPlaceholder}
          </>
        ) : (
          <>
            {leftIcon}
            {children}
          </>
        )}
      </button>
    );
  },
);
